// Shared QR value normalization used by every scan entry point (phone camera lands
// directly on the public route so it doesn't need this; the in-app mobile scanner,
// the add-tool form scanner, and the add-tool-to-worksite scanner all funnel scanned
// text through this helper instead of comparing raw scanned strings directly).

// Identifies what kind of code was scanned so callers can branch without re-parsing.
// TOOL is URL-based today (".../tools/overview/<barcode>"), and so is LOGGER_OVERVIEW,
// the QR printed on v3 loggers (".../loggers/overview/<barcode>", the public logger
// overview behind a verification code). LOGGER is the placeholder for everything not yet
// URL-based (currently: project/logger bare barcodes) - as more entities get their own QR
// URL format, give them their own BarcodeType value here instead of overloading LOGGER.
export enum BarcodeType {
  TOOL = 'TOOL',
  LOGGER = 'LOGGER',
  LOGGER_OVERVIEW = 'LOGGER_OVERVIEW',
}

export type ParsedQrValue = {
  /** The raw, unmodified text exactly as read from the QR code / scanner / manual input. */
  rawValue: string;
  /** The tool barcode: extracted from the URL when rawValue is a Kvalilog tool QR URL
   *  (".../tools/overview/<barcode>"); otherwise identical to rawValue. */
  barcodeValue: string;
  /** What kind of code this was recognized as - see BarcodeType. */
  type: BarcodeType;
};

const TOOL_OVERVIEW_PATTERN = /\/tools\/overview\/([^/?#]+)/i;
const LOGGER_OVERVIEW_PATTERN = /\/loggers\/overview\/([^/?#]+)/i;


export function parseQrValue(scanned: string): ParsedQrValue {
  const rawValue = (scanned || '').trim();
  const match = rawValue.match(TOOL_OVERVIEW_PATTERN);

  if (match) {
    return { rawValue, barcodeValue: decodeURIComponent(match[1]), type: BarcodeType.TOOL };
  }

  const loggerMatch = rawValue.match(LOGGER_OVERVIEW_PATTERN);

  if (loggerMatch) {
    return { rawValue, barcodeValue: decodeURIComponent(loggerMatch[1]), type: BarcodeType.LOGGER_OVERVIEW };
  }

  return { rawValue, barcodeValue: rawValue, type: BarcodeType.LOGGER };
}

export function buildToolQrUrl(barcode: string): string {
  return `${window.location.origin}/tools/overview/${encodeURIComponent(barcode)}`;
}

export function buildLoggerQrUrl(barcode: string): string {
  return `${window.location.origin}/loggers/overview/${encodeURIComponent(barcode)}`;
}
